import React, { useState, useEffect } from "react";
import { Pill, ShieldAlert, Check, Layers, User, Package, Archive, CheckCircle, Info, Globe, Search, ExternalLink, BookOpen, X } from "lucide-react";
import { Order, Patient, MedicalHistory } from "../types";
import { bnfCatalog, iraqiTradeNamesMap } from "../data/catalogs";


interface PharmacyDashboardProps {
  user: { email: string; name: string; role: string };
  apiToken: string;
}

export default function PharmacyDashboard({ user, apiToken }: PharmacyDashboardProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  
  const [generatedInfo, setGeneratedInfo] = useState<string>("");
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoTypeDisplay, setInfoTypeDisplay] = useState<string>("");

  const [interactionWarning, setInteractionWarning] = useState<string | null>(null);
  const [checkingInteraction, setCheckingInteraction] = useState(false);
  const [interactionCheckedPatientId, setInteractionCheckedPatientId] = useState<string | null>(null);

  // Pharmacy inventory logs simulation (linked to Iraqi EML drugs)
  const [inventoryStock, setInventoryStock] = useState<Record<string, number>>({
    "Retin-A 0.05% Cream Tubes": 24,
    "Diprogenta Topical Cream": 48,
    "Amoxicillin 500mg Caps": 120,
    "Cetirizine HCl 10mg Tabs": 180,
    "Clobetasol 0.05% Ointment": 35,
    "Mupirocin 2% Lipophilic Cream": 16,
  });

  // Dynamic State for Patient Medical History (Allergies and Chronic Conditions)
  const [selectedPatientHistory, setSelectedPatientHistory] = useState<MedicalHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // EXPERT COMMENTARY: Trusted Pharmacy Drug Directory Browser State (BNF & Iraqi EML catalogs)
  const [pharmacySearchTerm, setPharmacySearchTerm] = useState("");
  const [selectedBnfCategory, setSelectedBnfCategory] = useState("All");
  const [selectedBnfDrug, setSelectedBnfDrug] = useState<any | null>(null);

  // Clinic-validated CDSS scanning engine: checks for medication matches against allergies & chronic conditions
  const getClinicalConflicts = (instructionContent: string, history: MedicalHistory | null, patientId: string) => {
    const foundConflicts: { type: "ALLERGY" | "CHRONIC"; severity: "CRITICAL" | "HIGH" | "WARNING"; title: string; desc: string }[] = [];
    if (!instructionContent) return foundConflicts;
    const text = instructionContent.toLowerCase();

    // 1. Allergies dynamic check from Medical History
    const patientAllergies = history?.allergies || [];
    patientAllergies.forEach((alg: any) => {
      const allergen = alg.allergen.toLowerCase();
      // Safe boundary check & substring matching so we do not miss compound names
      if (text.includes(allergen) || (allergen.length > 3 && text.includes(allergen)) || allergen.split(/\s+/).some((word: string) => word.length > 3 && text.includes(word))) {
        foundConflicts.push({
          type: "ALLERGY",
          severity: alg.severity === "CRITICAL" || alg.severity === "SEVERE" ? "CRITICAL" : "HIGH",
          title: `Allergy Conflict: ${alg.allergen}`,
          desc: `Patient has a recorded allergy to ${alg.allergen} with ${alg.severity || "HIGH"} severity. Prescribed instruction contains: "${instructionContent}". Reaction: ${alg.reaction || "Symptomatic response"}`
        });
      }
    });

    // Fallback/additional verification against static patientAllergiesData registry mapping
    const staticAllergyStr = patientAllergiesData[patientId];
    if (staticAllergyStr && staticAllergyStr.toLowerCase() !== "none documented") {
      const matchesStatic = ["penicillin", "amoxicillin", "sulfonamides", "aspirin", "nsaid"].some(term => {
        return staticAllergyStr.toLowerCase().includes(term) && text.includes(term);
      });
      if (matchesStatic) {
        // Prevent duplicate allergen alerts if they are already flagged
        const isAlreadyFlagged = foundConflicts.some(c => c.type === "ALLERGY" && c.title.toLowerCase().includes("allergy"));
        if (!isAlreadyFlagged) {
          foundConflicts.push({
            type: "ALLERGY",
            severity: "CRITICAL",
            title: `Static Registry Allergy Match`,
            desc: `Patient's archived allergy notes: "${staticAllergyStr}" match the prescribed agent: "${instructionContent}".`
          });
        }
      }
    }

    // 2. Chronic Conditions Contraindications matching
    const chronicConditions = history?.chronicConditions || [];
    chronicConditions.forEach((cond: any) => {
      if (cond.status === "RESOLVED") return; // Skip historical resolved items
      const conditionName = cond.condition.toLowerCase();

      // Contraindication: Kidney/Renal Disease vs NSAIDs (Ibuprofen, Diclofenac, Naproxen, Aspirin, Meloxicam)
      if ((conditionName.includes("kidney") || conditionName.includes("renal")) && 
          (text.includes("ibuprofen") || text.includes("diclofenac") || text.includes("naproxen") || text.includes("aspirin") || text.includes("nsaid"))) {
        foundConflicts.push({
          type: "CHRONIC",
          severity: "CRITICAL",
          title: `Contraindication: Renal Impairment & NSAIDs`,
          desc: `NSAIDs (prescribed: "${instructionContent}") are nephrotoxic and contraindicated in active chronic kidney disease / renal impairment (${cond.condition}).`
        });
      }

      // Contraindication: Kidney/Renal Disease vs Metformin
      if ((conditionName.includes("kidney") || conditionName.includes("renal")) && text.includes("metformin")) {
        foundConflicts.push({
          type: "CHRONIC",
          severity: "HIGH",
          title: `Contraindication: Renal Impairment & Metformin`,
          desc: `Metformin (prescribed: "${instructionContent}") accumulates in renal insufficiency, increasing risk of lactic acidosis.`
        });
      }

      // Contraindication: Asthma/COPD vs Beta-Blockers (propranolol, atenolol, metoprolol, timolol, carvedilol)
      if ((conditionName.includes("asthma") || conditionName.includes("copd") || conditionName.includes("bronch")) && 
          (text.includes("propranolol") || text.includes("atenolol") || text.includes("metoprolol") || text.includes("timolol") || text.includes("carvedilol") || text.includes("beta blocker") || text.includes("beta-blocker"))) {
        foundConflicts.push({
          type: "CHRONIC",
          severity: "CRITICAL",
          title: `Contraindication: Reactive Airway Disease & Beta-Blockers`,
          desc: `Beta-blockers can cause bronchoconstriction and life-threatening bronchospasm in patients with reactive airway diseases (${cond.condition}).`
        });
      }

      // Contraindication: Peptic Ulcer Disease / Gastritis vs NSAIDs / Aspirin
      if ((conditionName.includes("ulcer") || conditionName.includes("gastric") || conditionName.includes("gastritis") || conditionName.includes("pud")) && 
          (text.includes("ibuprofen") || text.includes("diclofenac") || text.includes("naproxen") || text.includes("aspirin") || text.includes("meloxicam") || text.includes("nsaid"))) {
        foundConflicts.push({
          type: "CHRONIC",
          severity: "HIGH",
          title: `High Risk: Peptic Ulcer & Gastric Mucosal Injury`,
          desc: `NSAIDs block prostaglandins, causing critical mucosal degradation and gastrointestinal bleeding in patients with Gastric Ulcers (${cond.condition}).`
        });
      }

      // Contraindication: Diabetes vs Systemic Corticosteroids (Prednisolone, Dexamethasone, Hydrocortisone, Cortisone)
      if ((conditionName.includes("diabet") || conditionName.includes("dm")) && 
          (text.includes("prednisolone") || text.includes("dexamethasone") || text.includes("hydrocortisone") || text.includes("corticosteroid"))) {
        foundConflicts.push({
          type: "CHRONIC",
          severity: "WARNING",
          title: `Caution: Diabetes & Corticosteroids`,
          desc: `Corticosteroids trigger hyperglycemia and complicate glycemic containment in diabetic patients (${cond.condition}).`
        });
      }

      // Contraindication: Gout vs Hydrochlorothiazide (Thiazides)
      if (conditionName.includes("gout") && (text.includes("hydrochlorothiazide") || text.includes("thiazide"))) {
        foundConflicts.push({
          type: "CHRONIC",
          severity: "WARNING",
          title: `Caution: Gout & Thiazide Diuretics`,
          desc: `Thiazides compete with uric acid secretion, potentially precipitating severe gout flare-ups.`
        });
      }

      // Contraindication: Heart Failure vs NSAIDs
      if ((conditionName.includes("heart failure") || conditionName.includes("chf")) && 
          (text.includes("ibuprofen") || text.includes("diclofenac") || text.includes("naproxen") || text.includes("nsaid"))) {
        foundConflicts.push({
          type: "CHRONIC",
          severity: "HIGH",
          title: `Contraindication: Heart Failure & NSAID Fluid Retention`,
          desc: `NSAIDs promote sodium retention and peripheral vasoconstriction, worsening acute cardiac output failure.`
        });
      }
    });

    return foundConflicts;
  };

  useEffect(() => {
    fetchErxQueue();
  }, [apiToken]);

  // Secure reactive hook to fetch dynamic MedicalHistory for selected patient
  useEffect(() => {
    if (selectedOrder) {
      setHistoryLoading(true);
      fetch(`/api/patients/${selectedOrder.patientId}/medical-history`, {
        headers: { Authorization: `Bearer ${apiToken}` },
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Secure patient history fetch return code ${res.status}`);
          }
          return res.json();
        })
        .then((historyData) => {
          // Input sanitization and Clinical validator: ensure chronicConditions properties exist
          if (historyData) {
            if (!historyData.chronicConditions) historyData.chronicConditions = [];
            if (!historyData.allergies) historyData.allergies = [];
          }
          setSelectedPatientHistory(historyData);
        })
        .catch((err) => {
          console.error("Clinical audit error loading chronic conditions on Pharmacy Nodes:", err);
          setSelectedPatientHistory(null);
        })
        .finally(() => {
          setHistoryLoading(false);
        });
    } else {
      setSelectedPatientHistory(null);
    }
  }, [selectedOrder, apiToken]);

  useEffect(() => {
    if (selectedOrder && selectedOrder.patientId !== interactionCheckedPatientId) {
      setInteractionCheckedPatientId(selectedOrder.patientId);
      setInteractionWarning(null);
      
      const patientOrders = orders.filter(
        (o) => o.department === "PHARMACY" && o.patientId === selectedOrder.patientId && o.status === "PENDING"
      );
      
      const allInstructions = patientOrders.map(o => o.instruction).join("\n");
      
      const lines = allInstructions.split("\n").filter(l => l.trim().length > 2);
      const isMultiple = patientOrders.length > 1 || lines.length > 1;

      if (isMultiple) {
        setCheckingInteraction(true);
        fetch("/api/pharmacy-info", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
          body: JSON.stringify({ drugInstruction: allInstructions, infoType: 'INTERACTION' })
        })
        .then(res => res.json())
        .then(data => {
          if (data.info) {
             setInteractionWarning(data.info);
          }
        })
        .catch(err => console.error(err))
        .finally(() => setCheckingInteraction(false));
      }
    }
  }, [selectedOrder, orders, apiToken, interactionCheckedPatientId]);

  const fetchErxQueue = async () => {
    try {
      const oRes = await fetch("/api/orders", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const oData = await oRes.json();

      const pRes = await fetch("/api/patients", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const pData = await pRes.json();

      if (oRes.ok && pRes.ok) {
        setOrders(oData);
        setPatients(pData);

        // Auto select first pending pharmacy prescriptions
        const pendingErx = oData.filter((o: Order) => o.department === "PHARMACY" && o.status === "PENDING");
        if (pendingErx.length > 0 && !selectedOrder) {
          setSelectedOrder(pendingErx[0]);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDispense = async (orderId: string, drugName: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/orders/${orderId}/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          status: "DISPENSED",
          results: `Dispensed on ${new Date().toISOString()} under Iraqi EML regulatory compliance by Pharmacist Kawther.`,
        }),
      });

      if (res.ok) {
        setSuccessMsg(`eRx prescription committed and dispensed!`);
        setSelectedOrder(null);

        // Deduct inventory stock safely
        setInventoryStock((prev) => {
          const updated = { ...prev };
          const matchingKey = Object.keys(updated).find((k) =>
            drugName.toLowerCase().includes(k.split(" ")[0].toLowerCase())
          );
          if (matchingKey) {
            updated[matchingKey] = Math.max(0, updated[matchingKey] - 1);
          }
          return updated;
        });

        await fetchErxQueue();
        setTimeout(() => setSuccessMsg(""), 4000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchInfo = async (type: 'DRUG' | 'PATIENT_FOLLOWUP') => {
    if (!selectedOrder) return;
    setInfoLoading(true);
    setGeneratedInfo("");
    setInfoTypeDisplay(type === 'DRUG' ? 'Drug Information from Database' : 'إرشادات متابعة المريض (Arabic Patient Follow-up)');
    try {
      const res = await fetch("/api/pharmacy-info", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify({ drugInstruction: selectedOrder.instruction, infoType: type })
      });
      const data = await res.json();
      if (res.ok) {
        setGeneratedInfo(data.info);
      } else {
        setGeneratedInfo("Error retrieving information: " + data.error);
      }
    } catch (e) {
      setGeneratedInfo("Failed to connect to the server.");
    } finally {
      setInfoLoading(false);
    }
  };

  const filteredErx = orders.filter((o) => o.department === "PHARMACY");
  const pendingErxCount = filteredErx.filter((o) => o.status === "PENDING").length;

  // Static Patient Allergies record lookup mapping for Iraqi EHR database matching
  const patientAllergiesData: Record<string, string> = {
    p1: "None Documented",
    p2: "Penicillin/Amoxicillin (Triggers severe cutaneous hives)",
    p3: "Sulfonamides (Trimethoprim-Sulfamethoxazole compound)",
    p4: "Aspirin & NSAIDs (Asthma reactivation)",
  };

  const conflicts = selectedOrder
    ? getClinicalConflicts(selectedOrder.instruction, selectedPatientHistory, selectedOrder.patientId)
    : [];

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6">
      {/* Toast alert indicator */}
      {successMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#0F172A] text-white font-medium text-xs px-4 py-3 rounded-md shadow-md border border-[#E2E8F0] flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-[#10B981]" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Header Panel */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm mb-6 flex flex-col md:flex-row justify-between items-start md:md:items-center gap-4">
        <div>
          <span className="bg-[#F8FAFC] text-[#64748B] font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#E2E8F0]">
            eRx Pharmaceutical Division
          </span>
          <h1 className="text-base font-bold text-[#1E293B] mt-2">Iraqi EML Pharmacy Dispensation Node</h1>
          <p className="text-[#64748B] text-xs font-medium">Logged in as {user.name} • Kawther Dispense Ledger</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] text-[#0F172A] text-xs px-3 py-1.5 rounded-md flex items-center gap-2 font-bold select-none">
          <Package className="w-4 h-4" />
          <span>Pending eRx Items: {pendingErxCount}</span>
        </div>
      </div>

      {/* Summary Statistics Widget */}


      {/* Main layout splitting elements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Incoming prescriptions list */}
        {!selectedOrder && (
        <div className="lg:col-span-1 bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm">
          <h3 className="font-bold text-[#1E293B] text-xs mb-4 flex items-center gap-2 pb-2 uppercase tracking-wide text-[#64748B] border-b border-[#F1F5F9]">
            <Pill className="w-4 h-4 text-[#0F172A]" />
            Incoming eRx Prescriptions
          </h3>

          {filteredErx.length > 0 ? (
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
              {filteredErx.map((erx) => {
                const patientObj = patients.find((p) => p.id === erx.patientId);
                const isSelected = selectedOrder?.id === erx.id;

                return (
                  <div
                    key={erx.id}
                    onClick={() => setSelectedOrder(erx)}
                    className={`p-3 rounded-md border cursor-pointer select-none transition flex flex-col gap-1 bg-white border-[#E2E8F0] hover:bg-[#F8FAFC]`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[#64748B] font-mono">eRx ID: {erx.id.slice(0, 8)}</span>
                      <span className={`font-bold text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                        erx.status === "PENDING"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }`}>
                        {erx.status}
                      </span>
                    </div>
                    <strong className="text-[#1E293B] text-xs mt-1 block font-semibold">
                      {patientObj ? patientObj.name : "Unregistered patient"}
                    </strong>
                    <p className="text-[#64748B] text-xs truncate max-w-full line-clamp-1 font-medium">"{erx.instruction}"</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 border border-dashed border-[#E2E8F0] rounded-md">
              <Archive className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">eRx Queue is Empty</h4>
              <p className="text-[#64748B] text-xs mt-1 px-4 leading-relaxed font-medium">No incoming e-Prescriptions checked from physicians.</p>
            </div>
          )}
        </div>
        )}

        {/* Right column: Dispense interface and Stock Ledger */}
        <div className={`${!selectedOrder ? 'lg:col-span-2' : 'lg:col-span-3'} space-y-6`}>
          {selectedOrder ? (
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm space-y-6">
              <div className="flex border-b border-[#E2E8F0] pb-3 justify-between items-center bg-white">
                <div>
                  <h3 className="font-bold text-[#1E293B] text-xs">Review Active eRx Directive</h3>
                  <p className="text-[#64748B] text-[11px] mt-0.5">
                    For patient: <strong className="text-[#1E293B] font-bold">{patients.find((p) => p.id === selectedOrder.patientId)?.name}</strong>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[#64748B] text-[9px] font-medium uppercase tracking-wider">Reference: {selectedOrder.id.slice(0, 8)}</span>
                  <button 
                    type="button"
                    onClick={() => setSelectedOrder(null)}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition"
                  >
                    Close Record
                  </button>
                </div>
              </div>

              {/* Clinical Decision Support System (CDSS) - Safety & Contraindication Alerts */}
              <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-3">
                {/* Header of the Safety Shield */}
                <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                  <ShieldAlert className="w-4.5 h-4.5 text-rose-500" />
                  <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider">
                    Clinical Decision Support System (CDSS) Shield
                  </h4>
                  {historyLoading && (
                    <span className="text-[10px] text-slate-400 animate-pulse ml-auto">Syncing live EHR history...</span>
                  )}
                </div>

                {/* Patient's Base Profile Summary */}
                <div className="bg-white border border-slate-150 p-3 rounded text-[11px] grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-600">
                  <div>
                    <span className="font-bold text-slate-500 uppercase tracking-wide text-[9px] block mb-0.5">Documented Allergies Check</span>
                    <span className="text-slate-800 font-bold block bg-slate-50 px-2 py-1 rounded">
                      {selectedPatientHistory?.allergies && selectedPatientHistory.allergies.length > 0
                        ? selectedPatientHistory.allergies.map((a: any) => `${a.allergen} (${a.severity})`).join(", ")
                        : patientAllergiesData[selectedOrder.patientId] || "None Documented"}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-slate-500 uppercase tracking-wide text-[9px] block mb-0.5">Recorded Chronic Conditions</span>
                    <span className="text-slate-800 font-bold block bg-slate-50 px-2 py-1 rounded">
                      {selectedPatientHistory?.chronicConditions && selectedPatientHistory.chronicConditions.length > 0
                        ? selectedPatientHistory.chronicConditions.map((c: any) => `${c.condition} (${c.status})`).join(", ")
                        : "None Recorded"}
                    </span>
                  </div>
                </div>
                <div className="bg-white border border-slate-150 p-3 rounded text-[11px] text-slate-600 mt-2">
                  <span className="font-bold text-slate-500 uppercase tracking-wide text-[9px] block mb-0.5 flex items-center gap-1">
                    <Pill className="w-3 h-3 text-emerald-500" />
                    Active Chronic Medications
                  </span>
                  <span className="text-slate-800 font-bold block bg-slate-50 px-2 py-1 rounded">
                    {selectedPatientHistory?.chronicMedications && selectedPatientHistory.chronicMedications.length > 0
                      ? selectedPatientHistory.chronicMedications.map((m: any) => `${m.name} ${m.dose ? `(${m.dose})` : ''}`).join(", ")
                      : "None Recorded"}
                  </span>
                </div>


                {/* Detected Conflicts */}
                {conflicts.length > 0 ? (
                  <div className="space-y-2">
                    {conflicts.map((conflict, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-md border flex items-start gap-3 transition-all duration-300 ${
                          conflict.severity === "CRITICAL"
                            ? "bg-rose-50 border-rose-200 text-rose-900"
                            : conflict.severity === "HIGH"
                            ? "bg-amber-50 border-amber-200 text-amber-900"
                            : "bg-blue-50 border-blue-200 text-blue-900"
                        }`}
                      >
                        <ShieldAlert
                          className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                            conflict.severity === "CRITICAL"
                              ? "text-rose-600"
                              : conflict.severity === "HIGH"
                              ? "text-amber-600"
                              : "text-blue-600"
                          }`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-xs">{conflict.title}</span>
                            <span
                              className={`text-[8.5px] uppercase font-mono font-extrabold px-1.5 py-0.5 rounded border ${
                                conflict.severity === "CRITICAL"
                                  ? "bg-rose-100 text-rose-700 border-rose-300 animate-pulse"
                                  : conflict.severity === "HIGH"
                                  ? "bg-amber-100 text-amber-700 border-amber-300"
                                  : "bg-blue-100 text-blue-700 border-blue-300"
                              }`}
                            >
                              {conflict.severity}
                            </span>
                          </div>
                          <p className="text-[10px] mt-1 font-medium leading-relaxed opacity-95">
                            {conflict.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-md flex items-center gap-2 text-emerald-800">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <p className="text-[10px] font-bold">
                      No acute matching contraindications or clinical conflicts detected between prescribed drugs and patient profile. Safe for dispensation review.
                    </p>
                  </div>
                )}
              </div>

              {/* Interaction Warning Banner */}
              {(checkingInteraction || interactionWarning) && (
                <div className={`border p-4 rounded-md flex items-start gap-3 ${interactionWarning?.toLowerCase().includes('severe') || interactionWarning?.toLowerCase().includes('warning') || interactionWarning?.toLowerCase().includes('caution') || interactionWarning?.toLowerCase().includes('interact') ? 'bg-[#FFFBEB] border-[#FDE68A]' : 'bg-[#F0FDF4] border-[#BBF7D0]' }`}>
                  <Info className={`w-4 h-4 flex-shrink-0 mt-0.5 ${interactionWarning?.toLowerCase().includes('severe') || interactionWarning?.toLowerCase().includes('warning') || interactionWarning?.toLowerCase().includes('caution') || interactionWarning?.toLowerCase().includes('interact') ? 'text-[#D97706]' : 'text-[#16A34A]'}`} />
                  <div className="flex-1">
                    <h4 className={`font-bold text-xs uppercase tracking-wider ${interactionWarning?.toLowerCase().includes('severe') || interactionWarning?.toLowerCase().includes('warning') || interactionWarning?.toLowerCase().includes('caution') || interactionWarning?.toLowerCase().includes('interact') ? 'text-[#B45309]' : 'text-[#15803D]'}`}>
                      {checkingInteraction ? "Analyzing eRx Stack for Drug-Drug Interactions..." : "Automated Interaction Alert"}
                    </h4>
                    {interactionWarning && (
                      <div className={`mt-2 text-xs leading-relaxed whitespace-pre-wrap ${interactionWarning?.toLowerCase().includes('severe') || interactionWarning?.toLowerCase().includes('warning') || interactionWarning?.toLowerCase().includes('caution') || interactionWarning?.toLowerCase().includes('interact') ? 'text-[#92400E]' : 'text-[#166534]'}`}>
                        {interactionWarning}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Compound Instruction */}
              <div>
                <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5 font-semibold">Prescribed Substance & Regimen</label>
                <div className="bg-[#F8FAFC] p-4 border border-[#E2E8F0] rounded-md text-[#1E293B] font-mono font-semibold text-xs leading-relaxed select-all">
                  "{selectedOrder.instruction}"
                </div>
                <span className="text-[9px] text-[#64748B] font-mono mt-1.5 block">Signed by: {selectedOrder.physicianId.split('@')[0]}</span>
              </div>

              {/* AI Pharmacy Tools */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => handleFetchInfo('DRUG')}
                  disabled={infoLoading}
                  className="flex-1 bg-white border border-[#E2E8F0] hover:bg-slate-50 text-[#0F172A] font-bold text-xs py-2 px-3 rounded-md flex items-center justify-center gap-1.5 transition select-none disabled:opacity-50"
                >
                  <Info className="w-3.5 h-3.5" />
                  Drug Details & Dose
                </button>
                <button
                  type="button"
                  onClick={() => handleFetchInfo('PATIENT_FOLLOWUP')}
                  disabled={infoLoading}
                  className="flex-1 bg-[#F0FDF4] border border-[#DCFCE7] hover:bg-[#DCFCE7] text-[#15803D] font-bold text-xs py-2 px-3 rounded-md flex items-center justify-center gap-1.5 transition select-none disabled:opacity-50"
                >
                  <Globe className="w-3.5 h-3.5" />
                  Patient Follow-up (Arabic)
                </button>
              </div>

              {/* AI Info Display Panel */}
              {(generatedInfo || infoLoading) && (
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-md">
                  <h4 className="text-[10px] font-bold uppercase text-slate-500 tracking-wider mb-2 border-b border-slate-200 pb-1">{infoTypeDisplay}</h4>
                  {infoLoading ? (
                    <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                      <div className="w-4 h-4 border-2 border-slate-400 border-t-slate-800 rounded-full animate-spin"></div>
                      Fetching data...
                    </div>
                  ) : (
                    <p className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap font-medium" dir={infoTypeDisplay.includes('Arabic') ? 'rtl' : 'ltr'}>
                      {generatedInfo}
                    </p>
                  )}
                </div>
              )}

              {/* Dispensation trigger buttons */}
              {selectedOrder.status === "PENDING" ? (
                <button
                  type="button"
                  onClick={() => handleDispense(selectedOrder.id, selectedOrder.instruction)}
                  disabled={loading}
                  className="w-full h-10 bg-[#0F172A] hover:bg-slate-800 text-white font-bold text-xs rounded-md flex items-center justify-center gap-1.5 cursor-pointer transition select-none mt-4"
                >
                  <Check className="w-3.5 h-3.5" />
                  Validate Allergy Shield & Mark as Dispensed
                </button>
              ) : (
                <div className="bg-[#F0FDF4] p-3 rounded-md text-center text-[#15803D] font-bold text-xs border border-[#DCFCE7] uppercase tracking-wider mt-4">
                  Transaction Dispensed Successfully. Stock levels synced.
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 text-center py-12">
              <Pill className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <h3 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">Select Prescription Directive</h3>
              <p className="text-[#64748B] text-xs mt-1 font-medium">Select an active prescription item from the incoming row on the left.</p>
            </div>
          )}

          {/* EXPERT COMMENTARY: Trusted Clinical Drug Reference Directory (BNF & Iraqi EML) with clinical security, input sanitation, list suggestions, interactive monographs and trusted Drugs.com grounding connections */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-[#F1F5F9] pb-3">
              <div>
                <h3 className="font-bold text-[#1E293B] text-xs flex items-center gap-1.5 uppercase tracking-wide text-[#64748B]">
                  <Pill className="w-4 h-4 text-[#0F172A]" />
                  Trusted Drug Reference Directory Browser
                </h3>
                <p className="text-[11px] text-[#64748B] mt-0.5 font-medium">
                  Search 2,500+ British National Formulary (BNF) & Iraqi Essential Medicines (EML) drugs
                </p>
              </div>
              <div className="flex items-center gap-1">
                <span className="bg-[#EEF2F6] text-[#475569] font-mono text-[9px] font-extrabold px-2 py-0.5 rounded border border-[#E2E8F0] flex items-center gap-1 select-none">
                  <BookOpen className="w-3 h-3 text-[#64748B]" /> BNF 86 & WHO EML VERIFIED
                </span>
              </div>
            </div>

            {/* Live Search & Category Filtering Bar */}
            <div className="space-y-3">
              <div className="flex flex-col md:flex-row gap-3">
                {/* Search Input field with clean icon */}
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-[#94A3B8]" />
                  </div>
                  <input
                    type="text"
                    value={pharmacySearchTerm}
                    onChange={(e) => {
                      // Clinical Input Sanitation: sanitize input to safe characters (letters, numbers, spaces, dashes)
                      const sanitizedValue = e.target.value.replace(/[^a-zA-Z0-9\s-]/g, "");
                      setPharmacySearchTerm(sanitizedValue);
                    }}
                    placeholder="Search drug by generic name, local trade name, or therapeutic indication..."
                    className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-md pl-9 pr-8 py-2 text-xs font-semibold outline-none focus:border-[#0F172A] focus:bg-white transition"
                  />
                  {pharmacySearchTerm && (
                    <button
                      type="button"
                      onClick={() => setPharmacySearchTerm("")}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Categories Dropdown Filter */}
                <select
                  value={selectedBnfCategory}
                  onChange={(e) => setSelectedBnfCategory(e.target.value)}
                  className="bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:border-[#0F172A] cursor-pointer"
                >
                  <option value="All">All Therapeutic Categories</option>
                  <option value="Gastro-intestinal">Gastro-intestinal</option>
                  <option value="Cardiovascular">Cardiovascular</option>
                  <option value="Respiratory">Respiratory</option>
                  <option value="Central nervous system">Central nervous system</option>
                  <option value="Infections">Infections</option>
                  <option value="Endocrine">Endocrine</option>
                  <option value="Obstetrics, gynecology, and urinary-tract disorders">Obstetrics & Gynecology</option>
                  <option value="Nutrition and blood">Nutrition & Blood</option>
                  <option value="Musculoskeletal and joint diseases">Musculoskeletal & Joints</option>
                  <option value="Skin">Skin/Dermatology</option>
                </select>
              </div>

              {/* Quick Suggestion Pills */}
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mr-1">Frequent Searches:</span>
                {["Omeprazole", "Amoxicillin", "Atorvastatin", "Salbutamol", "Prednisolone"].map((pill) => (
                  <button
                    key={pill}
                    type="button"
                    onClick={() => setPharmacySearchTerm(pill)}
                    className="bg-white border border-[#E2E8F0] hover:bg-slate-50 text-slate-600 hover:text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded transition"
                  >
                    {pill}
                  </button>
                ))}
              </div>
            </div>

            {/* Filtered Drug Listing Content */}
            <div className="min-h-0">
              {(() => {
                // Perform dynamic lookup on directory
                let list = bnfCatalog || [];
                if (selectedBnfCategory !== "All") {
                  list = list.filter((item: any) => item.category === selectedBnfCategory);
                }
                if (pharmacySearchTerm.trim()) {
                  const cleanedTerm = pharmacySearchTerm.toLowerCase().trim();
                  list = list.filter((item: any) => {
                    const nameMatch = item.name.toLowerCase().includes(cleanedTerm);
                    const indMatch = (item.indication || "").toLowerCase().includes(cleanedTerm);
                    const infoMatch = (item.info || "").toLowerCase().includes(cleanedTerm);
                    const tradeNames = iraqiTradeNamesMap[item.name] || [];
                    const tradeMatch = tradeNames.some((tn: string) => tn.toLowerCase().includes(cleanedTerm));
                    return nameMatch || indMatch || infoMatch || tradeMatch;
                  });
                }

                // Slice list for visual performance representation (limit to 6 on general grid)
                const visibleCount = pharmacySearchTerm.trim() ? 12 : 6;
                const displayedList = list.slice(0, visibleCount);

                if (displayedList.length === 0) {
                  return (
                    <div className="text-center py-8 border border-dashed border-[#E2E8F0] rounded-md">
                      <Archive className="w-7 h-7 text-slate-300 mx-auto mb-1.5" />
                      <p className="text-[#64748B] text-xs font-semibold">No drugs matched the term: "{pharmacySearchTerm}"</p>
                      <button
                        type="button"
                        onClick={() => {
                          setPharmacySearchTerm("");
                          setSelectedBnfCategory("All");
                        }}
                        className="text-[11px] text-[#0F172A] font-extrabold underline mt-2 block mx-auto cursor-pointer"
                      >
                        Reset search filters
                      </button>
                    </div>
                  );
                }

                return (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {displayedList.map((drug: any) => {
                        const tradeNames = iraqiTradeNamesMap[drug.name] || [];
                        const inStockKeys = Object.keys(inventoryStock).filter((k) =>
                          k.toLowerCase().includes(drug.name.toLowerCase())
                        );
                        const isStocked = inStockKeys.length > 0;

                        return (
                          <div
                            key={drug.name}
                            onClick={() => setSelectedBnfDrug(drug)}
                            className="bg-[#F8FAFC] border border-[#E2E8F0] hover:border-slate-300 hover:shadow-sm rounded-md p-3.5 transition cursor-pointer select-none flex flex-col justify-between h-full"
                          >
                            <div>
                              <div className="flex justify-between items-start gap-2">
                                <span className="text-[9px] uppercase font-bold text-[#64748B] bg-white px-2 py-0.5 rounded border border-[#E2E8F0] tracking-wider truncate max-w-[130px]">
                                  {drug.category || "General"}
                                </span>
                                {isStocked ? (
                                  <span className="text-[8px] uppercase tracking-wide bg-emerald-50 text-emerald-700 font-extrabold border border-emerald-200 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                    <Check className="w-2.5 h-2.5" /> STOCKED
                                  </span>
                                ) : (
                                  <span className="text-[8px] uppercase tracking-wide bg-amber-50 text-amber-700 font-extrabold border border-amber-200 px-1.5 py-0.5 rounded">
                                    ORDERABLE
                                  </span>
                                )}
                              </div>

                              <h4 className="font-extrabold text-[#1E293B] text-xs mt-2 flex items-center gap-1">
                                {drug.name}
                              </h4>

                              {tradeNames.length > 0 && (
                                <p className="text-[10px] text-slate-500 font-semibold mt-1 font-sans">
                                  🇮🇶 Local Trade: <strong className="text-slate-700 font-bold">{tradeNames.join(", ")}</strong>
                                </p>
                              )}

                              <p className="text-[10.5px] text-[#64748B] line-clamp-2 mt-1.5 font-medium leading-relaxed">
                                {drug.indication}
                              </p>
                            </div>

                            <div className="flex items-center justify-between border-t border-[#E2E8F0]/60 mt-3 pt-2">
                              <span className="text-[9px] font-mono text-[#64748B] font-medium">{drug.defaultDose} • {drug.defaultFreq}</span>
                              <span className="text-[10px] text-[#0F172A] font-extrabold flex items-center gap-1 hover:underline">
                                Details & Links <ExternalLink className="w-2.5 h-2.5" />
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {list.length > visibleCount && (
                      <p className="text-[10px] text-[#64748B] text-center mt-3 font-semibold">
                        Showing top {visibleCount} of {list.length} matched directory. Refine search term or category to narrow results.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Interactive Drug Directory Explorer Detail Modal */}
          {selectedBnfDrug && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
              <div 
                className="bg-white border border-[#E2E8F0] rounded-xl w-full max-w-lg p-6 shadow-xl flex flex-col space-y-4 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Title Banner */}
                <div className="flex border-b border-[#E2E8F0] pb-4 justify-between items-start">
                  <div>
                    <span className="bg-[#EEF2F6] text-[#475569] font-extrabold text-[8.5px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#E2E8F0] select-none">
                      Trusted BNF Drug Monograph
                    </span>
                    <h3 className="font-extrabold text-[#1E293B] text-sm mt-1 flex items-center gap-1.5">
                      <Pill className="w-4 h-4 text-slate-800" />
                      {selectedBnfDrug.name}
                    </h3>
                  </div>
                  <button 
                    type="button"
                    onClick={() => setSelectedBnfDrug(null)}
                    className="p-1.5 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Substantive Drug Details */}
                <div className="space-y-4 text-xs">
                  {/* Category & Local Iraqi Trade Name */}
                  <div className="grid grid-cols-2 gap-3 bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]">
                    <div>
                      <span className="text-[9px] uppercase font-bold text-slate-400 block tracking-wider">Classification</span>
                      <span className="font-bold text-slate-800">{selectedBnfDrug.category || "General Therapeutic Agent"}</span>
                    </div>
                    <div>
                      <span className="text-[9px] uppercase font-bold text-slate-400 block tracking-wider">Iraqi Local Trade Names</span>
                      <span className="font-bold text-slate-800">
                        {iraqiTradeNamesMap[selectedBnfDrug.name]?.join(", ") || "No Specific Trade Mapping"}
                      </span>
                    </div>
                  </div>

                  {/* Standard Indication */}
                  <div>
                    <span className="text-[9px] uppercase font-bold text-slate-400 block tracking-wider mb-0.5">Approved Clinical Indication</span>
                    <p className="font-semibold text-slate-800 leading-relaxed bg-[#F8FAFC] p-2.5 rounded border border-[#E2E8F0]/80">
                      {selectedBnfDrug.indication}
                    </p>
                  </div>

                  {/* Standard Regimen Specs */}
                  <div className="grid grid-cols-3 gap-2 bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]">
                    <div>
                      <span className="text-[8px] uppercase font-bold text-slate-400 block">Default Single Dose</span>
                      <span className="font-bold text-slate-800 block mt-0.5 font-mono">{selectedBnfDrug.defaultDose}</span>
                    </div>
                    <div>
                      <span className="text-[8px] uppercase font-bold text-slate-400 block">Frequency Pattern</span>
                      <span className="font-bold text-slate-800 block mt-0.5 font-mono">{selectedBnfDrug.defaultFreq}</span>
                    </div>
                    <div>
                      <span className="text-[8px] uppercase font-bold text-slate-400 block">Standard Therapy duration</span>
                      <span className="font-bold text-slate-800 block mt-0.5 font-mono">{selectedBnfDrug.defaultDuration}</span>
                    </div>
                  </div>

                  {/* Pharmacological Monographs */}
                  {selectedBnfDrug.info && (
                    <div>
                      <span className="text-[9px] uppercase font-bold text-slate-400 block tracking-wider mb-0.5">Mechanism of Action & Counselling</span>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-line bg-white/50 p-2.5 border-l-2 border-slate-300 pl-2">
                        {selectedBnfDrug.info}
                      </p>
                    </div>
                  )}

                  {/* Side effects & Contraindications profile */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedBnfDrug.sideEffects && (
                      <div className="bg-amber-50/40 border border-amber-200/60 p-3 rounded-md">
                        <span className="text-[9px] uppercase font-bold text-amber-800 flex items-center gap-1 tracking-wider mb-1">
                          <Info className="w-3 h-3 text-amber-600" /> Common Side Effects
                        </span>
                        <p className="text-amber-900 leading-relaxed font-semibold text-[10.5px]">
                          {selectedBnfDrug.sideEffects}
                        </p>
                      </div>
                    )}
                    {selectedBnfDrug.contraindications && (
                      <div className="bg-rose-50/40 border border-rose-200/60 p-3 rounded-md">
                        <span className="text-[9px] uppercase font-bold text-rose-800 flex items-center gap-1 tracking-wider mb-1">
                          <ShieldAlert className="w-3 h-3 text-rose-600" /> Contraindications
                        </span>
                        <p className="text-rose-900 leading-relaxed font-semibold text-[10.5px]">
                          {selectedBnfDrug.contraindications}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Secure Reference Connections */}
                <div className="border-t border-[#E2E8F0] pt-4 flex flex-col sm:flex-row gap-3">
                  {/* External grounding validation to Drugs.com */}
                  <a
                    href={`https://www.drugs.com/search.php?searchterm=${encodeURIComponent(selectedBnfDrug.name)}`}
                    target="_blank"
                    rel="noreferrer referrer"
                    className="flex-1 bg-white border border-[#E2E8F0] hover:bg-slate-50 text-slate-800 text-[11px] font-extrabold py-2 rounded-lg flex items-center justify-center gap-1 transition select-none"
                  >
                    Verify on Drugs.com <ExternalLink className="w-3 h-3 text-slate-500" />
                  </a>
                  
                  {/* Action closing details */}
                  <button
                    type="button"
                    onClick={() => setSelectedBnfDrug(null)}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-extrabold py-2 rounded-lg transition cursor-pointer"
                  >
                    Close Monograph Reference
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Iraqi EML Pharmacy Stock Ledger */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm">
            <h3 className="font-bold text-[#1E293B] text-xs mb-4 flex items-center gap-1.5 uppercase tracking-wide text-[#64748B]">
              <Layers className="w-4 h-4 text-[#0F172A]" />
              Iraqi Essential Medicines Stock Ledger
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(inventoryStock).map(([drug, qty]) => {
                const stockQty = qty as number;
                return (
                  <div key={drug} className="bg-white border border-[#E2E8F0] p-3 rounded-md flex flex-col justify-between">
                    <span className="text-xs font-semibold text-[#1E293B] line-clamp-1">{drug}</span>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#F1F5F9]">
                      <span className="text-[10px] text-[#64748B] font-semibold uppercase tracking-wider">Stock Level:</span>
                      <strong className={`font-mono text-xs ${stockQty < 20 ? "text-rose-600 font-bold" : "text-emerald-700 font-bold"}`}>
                        {stockQty} units
                      </strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
